/* eslint-disable react/no-danger */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import AutosuggestComponent from 'react-autosuggest';
import cx from 'classnames';

import {
  getTranslation, Icon, Button, AriaLiveRegion, TranslationProvider, Link, Loader, IconBadge, utils,
} from '@resy/ui-kitchen';
import MapIcon from '../../../../../ui-kitchen/src/svg/icons/map.svg';
import Suggestion from '../Suggestion';
import KnifeFork from '../../../../../ui-kitchen/src/svg/icons/knife-fork.svg';
import { sanitizeString } from '../../../../../ui-kitchen/src/utils/sanitizeString';
import { selectExperiment } from '../../../../../ui-kitchen/src/components/Experiments/useExperiment';
import { JCE_INTEGRATION_WINERY } from '../../../../../ui-kitchen/src/constants/optimizelyExperiments';
import { VENUE_TYPE } from '../../../../../ui-kitchen/src/components/SearchFilterContainer/searchFilterConstants';
import { VenueTypeSearchPillGroup } from '../SearchVenueTypePills';
import './Autocomplete.scss';

export class Autosuggest extends AutosuggestComponent {
  /**
   * This class is a patch for the problems listed here:
   *
   *  https://github.com/moroshko/react-autosuggest/issues/609
   *
   * About all the problems the current onSuggestionTouchMove() has with
   * keyboards on MobileSafari.
   */
  constructor(props) {
    super(props);
    const self = this;
    self.onSuggestionTouchMove = () => {
      self.justSelectedSuggestion = false;
      self.pressedSuggestion = null;
    };
  }
}

const Autocomplete = ({
  Analytics,
  value,
  collections,
  locationName,
  locationCode,
  hits,
  facetValues,
  placeholder,
  isMobile,
  isOpen,
  isWineRegion,
  isVenueTypePillsEnabled,
  searchSessionId,
  onValueChange,
  onAutocompleteOpen,
  onCollectionSelected,
  onSuggestionSelected,
  onSearchRequested,
  onResetAutocompleteValue,
  onQuery,
}) => {
  const ref = React.useRef();
  const inputRef = React.useRef();
  useEffect(() => {
    const resultsContainer = ref.current?.getElementsByClassName('react-autosuggest__suggestions-list');
    if (resultsContainer && resultsContainer[0]) {
      resultsContainer[0].setAttribute('aria-label', 'Suggested search results');
    }
    const unorderedLists = ref.current?.querySelectorAll('ul');
    unorderedLists?.forEach((item) => {
      item.setAttribute('role', 'group');
    });
  }, [isOpen]);

  const scrollDetected = () => {
    inputRef.current.input.blur();
  };

  useEffect(() => {
    //  This is a workaround for a strange issue happening in safari mobile web.
    //  Unable to prevent the main body content from scrolling while a fixed position overlay is showing.
    //  Eventhough the overflow css property is set, when overlay is displayed, it’s not obeying this css property.
    //  Since this issue happening when keyboard is present, the solution is to dismiss the keyboard,
    //  when user starts scrolling the search overlay.
    let timeOutId;
    if (isMobile) {
      ref.current?.addEventListener('scroll', scrollDetected);
      //  When search overlay appears in mobile web, search field should be focussed automatically.
      //  If we directly set the focus, safari will zoom in the input field, hence setTimeout is used here.
      timeOutId = setTimeout(() => { inputRef.current?.input?.focus(); }, 100);
    }

    return () => {
      if (isMobile) {
        ref.current?.removeEventListener('scroll', scrollDetected);
        if (timeOutId) {
          clearTimeout(timeOutId);
        }
      }
    };
  }, []);

  const getSuggestionsForAriaLiveRegion = () => {
    if (!hits?.length) {
      return null;
    }

    const numResults = value ? hits.length + facetValues.length : 0;
    return numResults
      ? getTranslation({
        translationKey: 'search_aria_label',
        translationValues: { num: numResults, updateStr: value },
        fallback: `There are ${numResults} results from your query. Use the up and down arrows to navigate results. ${value}`,
      })
      : getTranslation({ translationKey: 'search_aria_label_no_results', fallback: 'No results.' });
  };

  const getSuggestions = () => {
    if (isOpen) {
      if (value) {
        if (hits?.length === 0) {
          return [[{ seeAllResults: true }]];
        }
        if (facetValues?.length > 0) {
          return [facetValues, hits, [{ seeAllResults: true }]];
        }
        return [hits, [{ seeAllResults: true }]];
      }

      const topSection = isWineRegion && isVenueTypePillsEnabled
        ? { venueTypePills: true }
        : { viewAllCityResults: true };

      if (collections) {
        const nonPlatinumCollections = collections?.filter((collection) => collection?.collectionSlug !== 'amex-platinum-nights');
        return [[topSection], [...nonPlatinumCollections.map((collection) => ({ collection: true, data: collection }))]];
      }

      return [[topSection], [{ loading: true }]];
    }

    return [];
  };

  const onOpen = () => {
    if (!isOpen) {
      onAutocompleteOpen();
    }
  };

  const onClose = () => {
    if (isOpen) {
      onAutocompleteOpen();
    }
  };

  const onChange = (_, { newValue, method }) => {
    onOpen();

    if (method === 'type') {
      onValueChange(newValue);
    }
  };

  const onBlur = (event) => {
    if (ref.current?.contains(event?.relatedTarget)) {
      return;
    }
    onClose();
  };

  const onFocus = () => {
    onOpen();
  };

  const onKeyPress = ({ key }) => {
    if (key === 'Enter') {
      onQuery(value);
    }
  };

  const onKeyDown = ({ key }) => {
    if (key === 'Escape') {
      onClose();
    }
  };

  const handleVenueTypeSelect = (key) => {
    if (key === 'all-venues') {
      onCollectionSelected();
    } else {
      onSuggestionSelected({ facet: true, type: VENUE_TYPE, value: key });
    }
  };

  const handleSuggestionSelected = (e, { suggestion }) => {
    e.preventDefault();

    if (suggestion.venueTypePills) {
      // The individual pills own their click handling.
      return;
    }

    if (suggestion.seeAllResults) {
      onQuery(value);
    } else if (suggestion.viewAllCityResults) {
      onCollectionSelected();
    } else if (suggestion.collection) {
      onCollectionSelected(suggestion.data);
    } else {
      onSuggestionSelected(suggestion);
    }
  };

  const handleLinkClick = (e) => {
    Analytics.event('complete search', {
      context: utils.getPageContext(window.location.pathname),
      'filter type': 'query',
      value: e.target.innerHTML,
      searchSessionId,
    });
  };

  const onSuggestionsFetchRequested = ({ reason, value: searchRequestValue }) => {
    if (reason === 'input-changed') {
      onSearchRequested(searchRequestValue);
    }
  };

  const renderMagnifyingGlassIcon = () => (
    <div className="MagnifyingGlass__container">
      <Icon name="MagnifyingGlass" />
    </div>
  );

  const renderCollectionTitle = (section) => {
    if (section && section.length && section[0]?.collection) {
      return (
        <div className="Autocomplete__collection-title">
          <TranslationProvider translationKey="search_collections_header">
            <h2 className="h6 color--text-secondary weight--normal">Resy Collections</h2>
          </TranslationProvider>
        </div>
      );
    }
    return null;
  };

  const renderSeeAllResultsLink = () => (
    <div className="Autocomplete__see-all-results">
      <div className="Autocomplete__see-all-results__inner-container">
        {renderMagnifyingGlassIcon()}
        <TranslationProvider translationKey="see_all_results_for_query" translationValues={{ searchString: value }}>
          <span>
            See all results for &quot;
            {value}
            &quot;&nbsp;&rsaquo;
          </span>
        </TranslationProvider>
      </div>
    </div>
  );

  const renderViewAllCityResultsLink = () => (
    <Link
      className="Autocomplete__view-all-link"
      onClick={handleLinkClick}
    >
      <Icon component={MapIcon} name="map" ariaHidden="true" />
      <TranslationProvider
        translationKey="search_view_all"
        translationValues={{
          city: locationName,
          code: locationCode,
        }}
      >
        <span>{`View all ${locationName} Restaurants`}</span>
      </TranslationProvider>
    </Link>
  );

  const renderVenueTypePills = () => (
    <VenueTypeSearchPillGroup onSelect={handleVenueTypeSelect} />
  );

  const renderSuggestion = (hit) => {
    if (hit?.collection) {
      const {
        data: {
          urlSlug, icon, name, shortName,
        },
      } = hit;

      return (
        <div className="Autocomplete__search-pill">
          <IconBadge url={icon} label={name} size={54} bgToSvgRatio={2} name={urlSlug === 'collection_14' ? 'gda' : name} title={name} background="#fafafa" />
          <p
            className={cx('SearchPill__label', {
              'SearchPill__label--gda': urlSlug === 'collection_14',
            })}
          >
            {shortName || name}
          </p>
        </div>
      );
    }
    if (hit.facet) {
      const sanitizedHighlighted = sanitizeString(hit.highlighted);
      return (
        <div className="Autocomplete__cuisine">
          <Icon component={KnifeFork} name="knife-fork" />
          <span dangerouslySetInnerHTML={{ __html: sanitizedHighlighted }} />
        </div>
      );
    }
    if (hit.seeAllResults) {
      return renderSeeAllResultsLink();
    }
    if (hit.viewAllCityResults) {
      return renderViewAllCityResultsLink();
    }
    if (hit.venueTypePills) {
      return renderVenueTypePills();
    }
    if (hit.loading) {
      return <Loader height={200} />;
    }

    return <Suggestion suggestion={hit} />;
  };

  const renderSuggestionsContainer = ({ containerProps, children, query }) => {
    const suggestionContainerProps = {
      ...containerProps,
      className: cx(containerProps.className, {
        'react-autosuggest__container--with-facets': facetValues.length > 0,
      }),
    };

    if (query === '') delete suggestionContainerProps.role;

    return (
      <div {...suggestionContainerProps} role="listbox">
        {children}
      </div>
    );
  };

  const renderClearIcon = () => (
    <Button
      className="Autocomplete__clear-container"
      onClick={onResetAutocompleteValue}
      /**
       * mouse clicking the close button wasn't firing the onClick event here because
       * the close button moves when the search input loses focus. Needed to use onMouseDown here
       * instead.
       */
      onMouseDown={onResetAutocompleteValue}
      variant="close"
      aria-label={getTranslation({
        translationKey: 'clear_search',
        fallback: 'Clear search',
      })}
    />
  );

  const getSectionSuggestions = (section) => section;
  const getSuggestionValue = (suggestion) => suggestion?.name || suggestion?.value;
  const shouldRenderSuggestions = () => true;

  const noop = () => {};
  const inputProps = {
    placeholder,
    onChange,
    onBlur: !isMobile ? onBlur : noop,
    onFocus: !isMobile ? onFocus : noop,
    onKeyPress,
    onKeyDown,
    value,
  };
  return (
    <div
      className={cx('Autocomplete', {
        'Autocomplete--mobile': isMobile,
        'Autocomplete--desktop': !isMobile,
        'Autocomplete--open': isOpen,
        'Autocomplete--results': value !== '',
        'Autocomplete--rest': value === '' && !!collections,
        'Autocomplete--loading': value === '' && !collections,
        'Autocomplete--no-location--open': value === '' && !locationName && isOpen,
        'Autocomplete--venue-type-pills': isWineRegion && isVenueTypePillsEnabled,
      })}
      ref={ref}
    >
      <AriaLiveRegion>{getSuggestionsForAriaLiveRegion()}</AriaLiveRegion>
      {renderMagnifyingGlassIcon()}
      <Autosuggest
        alwaysRenderSuggestions
        getSectionSuggestions={getSectionSuggestions}
        getSuggestionValue={getSuggestionValue}
        inputProps={inputProps}
        multiSection
        onSuggestionSelected={handleSuggestionSelected}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        ref={inputRef}
        renderSectionTitle={renderCollectionTitle}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        shouldRenderSuggestions={shouldRenderSuggestions}
        suggestions={getSuggestions()}
      />
      {value && renderClearIcon()}
    </div>
  );
};

Autocomplete.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  collections: PropTypes.arrayOf(PropTypes.object),
  facetValues: PropTypes.arrayOf(PropTypes.shape({
    facet: PropTypes.bool,
    highlighted: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  hits: PropTypes.arrayOf(PropTypes.shape({
    cuisine: PropTypes.string,
    id: PropTypes.number,
    image: PropTypes.string,
    name: PropTypes.string,
    urlSlug: PropTypes.string,
    highlighted: PropTypes.shape({
      city: PropTypes.string,
      cuisine: PropTypes.string,
      neighborhood: PropTypes.string,
      state: PropTypes.string,
    }),
    _geoloc: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    location: PropTypes.shape({
      city: PropTypes.string,
      code: PropTypes.string,
      name: PropTypes.string,
      neighborhood: PropTypes.string,
      state: PropTypes.string,
    }),
  })).isRequired,
  isMobile: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isWineRegion: PropTypes.bool,
  isVenueTypePillsEnabled: PropTypes.bool,
  locationCode: PropTypes.string,
  locationName: PropTypes.string,
  onAutocompleteOpen: PropTypes.func.isRequired,
  onCollectionSelected: PropTypes.func.isRequired,
  onQuery: PropTypes.func.isRequired,
  onResetAutocompleteValue: PropTypes.func.isRequired,
  onSearchRequested: PropTypes.func.isRequired,
  onSuggestionSelected: PropTypes.func.isRequired,
  onValueChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  searchSessionId: PropTypes.string,
  selectedQuery: PropTypes.string,
  value: PropTypes.string.isRequired,
};

Autocomplete.defaultProps = {
  collections: undefined,
  isWineRegion: false,
  isVenueTypePillsEnabled: false,
  placeholder: null,
  selectedQuery: null,
  locationCode: null,
  locationName: null,
  searchSessionId: '',
};

const mapStateToProps = (state) => {
  const { venueSearch, resyLocations } = state;
  const { enabled: isVenueTypePillsEnabled = false } = selectExperiment(
    state,
    JCE_INTEGRATION_WINERY
  );
  return {
    searchSessionId: venueSearch?.metadata?.searchSessionId,
    isWineRegion: !!resyLocations?.selected?.is_wine_region,
    isVenueTypePillsEnabled,
  };
};

Autocomplete.displayName = 'Autocomplete';

export default connect(mapStateToProps)(Autocomplete);
